import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AssignmentCard } from '../../components/mobile/AssignmentCard';
import { AssignmentSheet } from '../../components/mobile/AssignmentSheet';
import { StaleScheduleNotice } from '../../components/schedule/StaleScheduleNotice';
import {
  MobileEmptyState,
  MobileErrorState,
  MobileListSkeleton,
} from '../../components/mobile/MobileListStates';
import { useMySchedule } from '../../hooks/useMySchedule';
import { ApiError } from '../../lib/api';
import { addDays, formatShort, formatShortWithYear, parseIsoDate, todayIso } from '../../lib/date';
import type { ScheduleCellAssignment, ScheduleGridResponse } from '../../lib/types';

/** Kept in one place so the query window and the "Next 90 days" copy can't drift apart. */
const WINDOW_DAYS = 90;

/**
 * The shared offline copy talks about "the last loaded day", which is right for the two
 * day-at-a-time views and wrong here — this agenda is a 90-day span, not a day.
 */
const OFFLINE_BODY =
  'Reconnect to load your schedule. The assignments already loaded stay on screen.';

interface AgendaItem {
  assignment: ScheduleCellAssignment;
  /** The earliest day this assignment appears on in the response — decides which week it sits in. */
  firstDay: string;
  /** Already running when the window opened, so its start date is in the past. */
  inProgress: boolean;
}

interface WeekGroup {
  weekStart: string;
  items: AgendaItem[];
}

/** Sunday-start week containing `iso`, matching the desktop page's grouping. */
function weekStartOf(iso: string): string {
  return addDays(iso, -parseIsoDate(iso).getDay());
}

/**
 * `This week` / `Next week` for the two the user cares about, then a dated label. The year
 * appears only when the week falls outside the current one — inside a 90-day window that's
 * rare, and printing it every time would make every header wider for nothing.
 */
function weekLabel(weekStart: string): string {
  const today = todayIso();
  if (weekStart === weekStartOf(today)) return 'This week';
  if (weekStart === weekStartOf(addDays(today, 7))) return 'Next week';
  const sameYear = weekStart.slice(0, 4) === today.slice(0, 4);
  return `Week of ${sameYear ? formatShort(weekStart) : formatShortWithYear(weekStart)}`;
}

/**
 * Collapse the day-expanded response into one entry per assignment.
 *
 * `/api/me/schedule` emits an item per *day*, so a one-week job arrives as seven identical
 * items. Rendering those verbatim — which the desktop page does — turns fourteen jobs into
 * seventy cards. Deduping by `assignmentId` and leaning on the DTO's `plannedStart` /
 * `plannedEnd` gives one card that states its own range instead.
 *
 * Each assignment lands in the week of the earliest day it appears on, and never repeats in
 * the weeks it continues into: the card's visible date range already says it runs on.
 */
function collapseToAgenda(data: ScheduleGridResponse): AgendaItem[] {
  const row = data.rows[0];
  if (!row) return [];

  // Sorted, not `data.days`-indexed: iterating in date order is what makes "first occurrence
  // wins" mean "earliest day", and it avoids a find() per day over the whole cell list.
  const cells = [...row.cells].sort((a, b) => a.date.localeCompare(b.date));
  const windowStart = data.days[0] ?? cells[0]?.date;

  const seen = new Set<number>();
  const agenda: AgendaItem[] = [];
  for (const cell of cells) {
    for (const assignment of cell.items) {
      if (seen.has(assignment.assignmentId)) continue;
      seen.add(assignment.assignmentId);
      agenda.push({
        assignment,
        firstDay: cell.date,
        // A start date before the window isn't a data error, it's a job already underway —
        // the badge says so, so the past date doesn't read as one.
        inProgress: !!windowStart && !!assignment.plannedStart && assignment.plannedStart < windowStart,
      });
    }
  }
  return agenda;
}

/** Agenda items bucketed into weeks, in date order, each week appearing exactly once. */
function groupByWeek(agenda: AgendaItem[]): WeekGroup[] {
  const weeks: WeekGroup[] = [];
  for (const item of agenda) {
    const weekStart = weekStartOf(item.firstDay);
    const current = weeks[weeks.length - 1];
    if (current?.weekStart === weekStart) current.items.push(item);
    else weeks.push({ weekStart, items: [item] });
  }
  return weeks;
}

/**
 * `/me` on a phone: a full-width agenda of the next 90 days.
 *
 * Two things separate it from the desktop page. The `w-72` mini-calendar sidebar is gone —
 * at 393px it squeezed the list it was meant to caption. And the day-expanded duplicates are
 * collapsed, so a multi-day job is one card with a range rather than one card per day.
 */
export function MyScheduleMobilePage() {
  const [sheetJob, setSheetJob] = useState<string | null>(null);
  // Which failure the user has already waved away, by its timestamp — a later failure gets
  // to interrupt again, the same one doesn't.
  const [dismissedErrorAt, setDismissedErrorAt] = useState<number | null>(null);

  const query = useMySchedule(WINDOW_DAYS);

  const data = query.data;
  const personRow = data?.rows[0] ?? null;

  // A 404 means "this account has no crew member behind it" — a fact about the account, not a
  // failure to answer. It gets its own explanation ahead of the generic error block.
  const is404 = query.error instanceof ApiError && query.error.status === 404;
  const noProfile = is404 || (!!data && !personRow);

  const hasCachedData = !!data;
  const showErrorBlock = !!query.error && !is404 && !hasCachedData;
  // A schedule already on screen beats a blank screen in the field, so a failure with cached
  // data is a strip over the stale list rather than a replacement for it.
  const showErrorStrip =
    !!query.error && !is404 && hasCachedData && query.errorUpdatedAt !== dismissedErrorAt;

  const agenda = data ? collapseToAgenda(data) : [];
  const weeks = groupByWeek(agenda);

  // The deduped count — the number of jobs, which is what the user would count by hand. The
  // day-expanded figure would read "70" for fourteen jobs.
  const countText =
    agenda.length === 0
      ? 'No assignments'
      : agenda.length === 1
        ? '1 assignment'
        : `${agenda.length} assignments`;
  const subtitle = `Next ${WINDOW_DAYS} days · ${countText}${
    personRow?.region ? ` · ${personRow.region}` : ''
  }`;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* No in-page title here — the shell's title bar already says "My Schedule", and the
          sibling mobile pages don't repeat their own title either. Only the subtitle line
          (or nothing at all) needs to render. */}
      {!noProfile && (
        <div className="shrink-0 bg-white border-b border-slate-200 px-3 py-3">
          <p className="text-xs text-brand-charcoal/70">{subtitle}</p>
        </div>
      )}

      <StaleScheduleNotice />

      <div className="flex-1 overflow-y-auto overscroll-contain">
        {showErrorStrip && (
          <MobileErrorState
            title="Couldn't load your schedule"
            onRetry={() => void query.refetch()}
            isRetrying={query.isFetching}
            compact
            onDismiss={() => setDismissedErrorAt(query.errorUpdatedAt)}
            offlineBody={OFFLINE_BODY}
          />
        )}

        {showErrorBlock && (
          <MobileErrorState
            title="Couldn't load your schedule"
            onRetry={() => void query.refetch()}
            isRetrying={query.isFetching}
            offlineBody={OFFLINE_BODY}
          />
        )}

        {!showErrorBlock && !noProfile && query.isLoading && <MobileListSkeleton blocks={2} />}

        {noProfile && (
          <MobileEmptyState
            title="No crew profile linked"
            body="Your account isn't linked to a crew member profile, so there's no schedule to show."
          >
            {/* One flex item, so the second paragraph stacks above the action instead of
                sitting beside it in the empty state's action row. */}
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-slate-500 max-w-[280px]">
                Ask your admin to check that your email matches your name in the schedule source.
              </p>
              <Link
                to="/schedule"
                className="inline-flex items-center min-h-[44px] px-4 rounded-lg border border-slate-300 text-sm text-brand-charcoal"
              >
                View the full schedule
              </Link>
            </div>
          </MobileEmptyState>
        )}

        {!showErrorBlock && !noProfile && !query.isLoading && weeks.length === 0 && (
          <MobileEmptyState
            title="No assignments"
            body={`Nothing is scheduled for you in the next ${WINDOW_DAYS} days.`}
          />
        )}

        {!showErrorBlock &&
          !noProfile &&
          weeks.map((week) => (
            <section key={week.weekStart} aria-labelledby={`wk-${week.weekStart}`}>
              <h2
                id={`wk-${week.weekStart}`}
                className="sticky top-0 z-10 text-xs font-semibold uppercase tracking-wide text-slate-500 bg-slate-50 px-3 py-2 border-y border-slate-200"
              >
                {weekLabel(week.weekStart)}
              </h2>
              <ul>
                {week.items.map(({ assignment, inProgress }) => (
                  <li key={assignment.assignmentId}>
                    <AssignmentCard
                      assignment={assignment}
                      dateMode="range"
                      inProgress={inProgress}
                      onClick={() => setSheetJob(assignment.jobNumber)}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
      </div>

      {/* A sheet, not a route change: `/projects/{job}` would drop the user's place in a
          90-day list and leave the OS back gesture as the only way home. `View full project →`
          inside the sheet still gets them there. */}
      <AssignmentSheet jobNumber={sheetJob} onClose={() => setSheetJob(null)} />
    </div>
  );
}
